import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { ApiModule, Configuration, ExternalService } from '@finra/angular-data-collection-sdk';
import { Configuration as FistConfiguration } from '@finra/angular-fist-sdk';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { GATEWAY_BASE_PATH, GatewayLocationService } from 'lib-services';
import {
  AppErrorHandlerService,
  DATACOLLECTION_INTERCEPTOR_URL,
  ERROR_HANDLER_APP_NAME,
  FinHttpInterceptor,
  FIST_INTERCEPTOR_URL,
  HTTP_CACHE_POLICY_OPTIONS,
  RampdiFormsCommonModule,
} from '@finra/rampdi-forms-common';
import { APP_BASE_HREF, Location } from '@angular/common';
import { BeastClientModule } from '@finra/beast-client';
import { AppRoutingModule } from './app-routing.module';
import { FooterComponent } from './components/footer/footer.component';
import { FormSelectorComponent } from './components/form-selector/form-selector.component';
import { RouteNoopComponent } from './components/route-noop/route-noop.component';
import { ErrorRouteComponent } from './components/error-route/error-route.component';
import { PostSubmissionComponent } from './components/post-submission/post-submission.component';
import { GlobalContext } from './services/global-context.service';
import { SharedStateService } from './services/shared-state.service';
import { HostApiService } from 'lib-host-interface';
import { hostApiMockService } from 'projects/app-mu3-form/mock/hostApi';
import { FinraDefaultFormComponents } from '@finra/forms';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyJsonschema } from '@ngx-formly/core/json-schema';

export function configFactory(api: string) {
  return () => {
    const config = new Configuration();
    config.basePath = environment.urls[api];
    config.withCredentials = true;
    return config;
  };
}

const ComponentsOverride: any = FinraDefaultFormComponents.map((item) => {
  (item.defaultOptions as any) = item.name === 'string' ? { modelOptions: { updateOn: 'blur' } } : item.defaultOptions;
  return item;
});

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    FormSelectorComponent,
    RouteNoopComponent,
    ErrorRouteComponent,
    PostSubmissionComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ApiModule,
    BeastClientModule,
    RampdiFormsCommonModule,
    FormlyModule.forChild({
      types: [...ComponentsOverride],
    }),
  ],
  providers: [
    {
      provide: FormlyJsonschema,
      useFactory: () => {
        const base = new FormlyJsonschema();
        (base as any).isFieldValid = (f, i, schemas, options) => {
          const schema = schemas[i];
          return f.model.type === schema.properties.type.const;
        };
        return base;
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (es: ExternalService) => {
        return () => {
          return es.getCsrfTokenUsingGET();
        };
      },
      multi: true,
      deps: [ExternalService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (gc: GlobalContext) => {
        return () => {
          // @mu3-todo(workaround): IAM 403 bypass — remove once MU3 is onboarded to identity gateway
          // Workaround: catch identity 403 to allow app to load while MU3 IAM onboarding is pending
          return Promise.all([gc.getModProfile(), gc.getClassification(), gc.getPermissions(), gc.getUserInfo()]).catch(
            (err) => {
              console.warn(
                '[app-mu3-form] Identity initialization failed (IAM onboarding pending):',
                err?.message || err,
              );
              return [];
            },
          );
        };
      },
      multi: true,
      deps: [GlobalContext],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FinHttpInterceptor,
      multi: true,
    },
    {
      provide: GATEWAY_BASE_PATH,
      useValue: environment.dxtBasePath,
    },
    {
      provide: Location,
      useClass: GatewayLocationService,
    },
    {
      provide: ErrorHandler,
      useClass: AppErrorHandlerService,
    },
    {
      provide: ERROR_HANDLER_APP_NAME,
      useValue: 'app-mu3-form',
    },
    {
      provide: APP_BASE_HREF,
      deps: [],
      useFactory: () => {
        if (globalThis.APP_MU3_FORM_INTEGRATION === 'standalone') {
          return environment.baseHref;
        } else {
          return environment.dxtBaseHref;
        }
      },
    },
    {
      provide: Configuration,
      useFactory: configFactory('datacollection'),
      multi: false,
    },
    {
      provide: FistConfiguration,
      useFactory: configFactory('fist'),
      multi: false,
    },
    {
      provide: DATACOLLECTION_INTERCEPTOR_URL,
      useFactory: (dcsConfig: Configuration) => {
        return dcsConfig.basePath;
      },
      deps: [Configuration],
    },
    {
      provide: FIST_INTERCEPTOR_URL,
      useFactory: (fistConfig: FistConfiguration) => {
        return fistConfig.basePath;
      },
      deps: [FistConfiguration],
    },
    {
      provide: HTTP_CACHE_POLICY_OPTIONS,
      useValue: {
        cacheResponseCodes: [200],
        urlPattern: /\.html$|LicenseTypeAndRequirements|LicenseRequirements|LicenseType/,
      },
      multi: false,
    },
    {
      provide: HostApiService,
      useClass: environment.mockHostApi ? hostApiMockService : HostApiService,
    },
    GlobalContext,
    { provide: SharedStateService, useClass: SharedStateService },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(private injector: Injector) {}

  ngDoBootstrap() {
    const customEl = createCustomElement(AppComponent, { injector: this.injector });
    customElements.define('finra-mu3-form', customEl);

    const customElForHeader = createCustomElement(HeaderComponent, { injector: this.injector });
    customElements.define('finra-mu3-header', customElForHeader);
  }
}
